<template>
  <div class="PostalNumber" :class="{ isPc: !isSp, hasError: hasError }">
    <div class="inputWithLabel" :class="{ isPc: !isSp }">
      <InputLabel
        class="labelArea"
        :class="{ isPc: !isSp, isHasLabel: !isSp && (required || optional) }"
        :required="required"
        :optional="optional"
      >
        郵便番号
      </InputLabel>
      <InputItem
        v-model="handleInputValue"
        class="inputItem"
        input-type="text"
        placeholder-value="例）1234567（ハイフンなし）"
        :class="{ hasError: hasError, isPc: !isSp }"
        :disabled="disabled"
        @blur="onBlur"
        @focus="onFocus"
        @input="onInput"
      />
    </div>
    <div class="underInputArea" :class="{ isPc: !isSp }">
      <SuggestList
        v-show="isDisplaySuggest && suggestList.address.length !== 0"
        :list="arrangedSuggestList"
        class="suggestArea"
        :class="{ isPc: !isSp }"
        dense
        @onClick="onClick"
      />
      <PartialErrorMessage :is-sp-flg="true" :error="displayError" class="partialErrorComponent" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { throttle } from 'throttle-debounce';
import InputLabel from '../Text/InputLabel.vue';
import InputItem from './InputItem.vue';
import PartialErrorMessage from '../PartialErrorMessage.vue';
import { isAllNumber, checkWordCount, isEmpty } from '../../../../helper/_validationCheck';
import { SPECIFY_VALIDATION_MESSAGE, GENERAL_VALIDATION_MESSAGE } from '../../../../helper/_validationCheckMessages';
import SuggestList from './Suggest/SuggestList.vue';

const MIN_MAX_LIST = {
  POSTAL_NUMBER: {
    MIN: 7,
    MAX: 7,
  },
};

export default {
  name: 'PostalNumber',
  components: {
    InputLabel,
    InputItem,
    PartialErrorMessage,
    SuggestList,
  },
  model: {
    prop: 'inputValue',
    event: 'change',
  },
  props: {
    inputValue: {
      type: String,
      required: true,
    },
    /**
     * 外部からのエラー
     * このコンポーネントに定義しないような独自のバリデーションを持つ場合、
     * 親コンポーネントでv-modelの値をwatchとかしてバリデーションチェックを行い、
     * errorFromOuterにpropsで渡すと、最後の項目としてチェック入る。
     * 空文字・undefinedを渡すことで『エラー無し』扱いになる。
     */
    errorFromOuter: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    optional: {
      type: Boolean,
      required: false,
      default: false,
    },
    postalClickFlg: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSpFlg: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    validateAtMount: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isDisplaySuggest: false,
      validationError: undefined,
      suggestList: {
        address: [],
      },
      // 【IE対応】IEでのみ、inputが初期入力時に発火し、サジェストがでてしまうのを防ぐためのもの
      focusFlg: false,
    };
  },
  computed: {
    isSp() {
      if (typeof this.isSpFlg === 'boolean') return this.isSpFlg;
      return this.$isSp;
    },
    arrangedSuggestList() {
      return this.suggestList.address.map((item, index) => ({
        id: index,
        name: `〒${item.zipNo} ${item.addr1}${item.addr2}${item.addr3}`,
        value: item,
      }));
    },
    displayError() {
      if (this.validationError) return this.validationError;
      return this.errorFromOuter;
    },
    hasError() {
      // displayError自体がundefinedならエラーない
      if (!this.displayError) return false;
      // displayError自体がlengthもってるならエラーあり
      if (this.displayError.length) return true;
      return false;
    },
    handleInputValue: {
      get() {
        return this.inputValue;
      },
      set(value) {
        this.$emit('change', value);
      },
    },
  },
  watch: {
    postalClickFlg() {
      // 郵便番号が変更されたタイミングでこのフラグが発火して再度バリデチェックする
      this.validCheck();
    },
  },
  mounted() {
    if (this.validateAtMount) {
      this.onBlur();
    }
  },
  methods: {
    onInput(event) {
      if (this.focusFlg === false) return;
      this.displaySuggest(event.target.value);
    },
    getSuggestList: throttle(500, function getSuggestList(value) {
      // すべて半角数字になってない場合は無駄打ちになるので処理しない
      if (!isAllNumber(value)) return;
      const param = {
        zipNo1: value.substr(0, 3),
        zipNo2: value.substr(3, 4),
      };
      axios
        .post('/net2/addressSearch/addressSearchJson', qs.stringify(param))
        .then((response) => {
          if (!response) throw Error;
          if (!response.data) throw Error;
          this.suggestList.address = response.data.address;
        })
        .catch(() => {
          this.suggestList.address = [];
        });
    }),
    onClick(emitValue) {
      this.isDisplaySuggest = false;
      this.$emit('change', emitValue.value.zipNo);
      this.$emit('onClick', emitValue.value);
    },
    generateValidationCheckMessage() {
      // 必須ではない、かつ、空文字の場合はバリデーションチェックしない
      if (!this.required && isEmpty(this.inputValue)) return undefined;
      if (isEmpty(this.inputValue)) {
        return `郵便番号${GENERAL_VALIDATION_MESSAGE.EMPTY}`;
      }
      if (!checkWordCount(this.inputValue, MIN_MAX_LIST.POSTAL_NUMBER.MIN, MIN_MAX_LIST.POSTAL_NUMBER.MAX)) {
        return SPECIFY_VALIDATION_MESSAGE.POSTAL_NUMBER.WORD_COUNT_ERROR;
      }
      if (!isAllNumber(this.inputValue)) {
        return `郵便番号${GENERAL_VALIDATION_MESSAGE.ONLY_HALF_WIDTH_NUMBER}`;
      }
      return undefined;
    },
    validCheck() {
      this.validationError = this.generateValidationCheckMessage();
      this.$emit('watchValidation', !!this.validationError);
    },
    displaySuggest(value) {
      const currentValue = value;
      if (currentValue.length > 5) {
        this.isDisplaySuggest = true;
        this.getSuggestList(currentValue);
      } else {
        this.isDisplaySuggest = false;
      }
    },
    onFocus(event) {
      this.$emit('focus');
      this.focusFlg = true;
      this.displaySuggest(event.target.value);
    },
    onBlur() {
      this.isDisplaySuggest = false;
      this.focusFlg = false;
      this.validCheck();
      this.$emit('blur');
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'css/contents/common/global';

$input-item-width: 316px;

.PostalNumber {
  .inputWithLabel {
    .labelArea.isPc {
      width: 120px;
      margin: auto 0;

      &.isHasLabel {
        width: 184px;
      }
    }

    .inputItem {
      &.isPc {
        width: $input-item-width;
      }

      &.hasError,
      &.hasError:focus {
        border: $border-alert;
      }
    }

    &.isPc {
      display: flex;
    }
  }

  .underInputArea {
    &.isPc {
      margin-left: 184px;
    }

    .suggestArea.isPc {
      width: $input-item-width;
    }

    .partialErrorComponent {
      background: $color-background-primary-third-light;
      margin: 8px 0;
    }
  }
}
</style>
